// Mercado de jogadores (estilo Market do Tibia Global), com visual de balcão de comerciante: à esquerda as categorias, filtros
// e a grade de itens; à direita o item escolhido (Ofertas / Detalhes / Estatísticas) e o painel "Anunciar". Em "Minhas
// ofertas", as ofertas abertas (com Cancelar) e o histórico. As Noxara Coins são negociadas aqui por gold, como as Tibia Coins.
// CSS em client/css-parts/market.css (prefixo .mkt-; o .mk- antigo já existe em styles.css).
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { ItemInstance, ItemType } from '../../../../shared/src/game/items';
import { RARITY_NAMES, describeItem } from '../../../../shared/src/game/items';
import {
  MARKET_CATEGORIES, MARKET_COIN_ITEM, MARKET_MAX_PRICE, MARKET_OFFER_DAYS, marketCategory, marketFee,
  type MarketCurrency, type MarketHistoryRow, type MarketOffer, type MarketStatSide, type MarketStats,
} from '../../../../shared/src/game/market';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';

type Op = 'sell' | 'sellCoins' | 'buy' | 'cancel' | 'createBuy' | 'sellTo' | 'history' | 'stats';
type Send = (m: { op: Op; itemId?: number; offerId?: number; typeId?: number; count?: number; price?: number; anonymous?: boolean; currency?: MarketCurrency }) => void;
const n0 = (n: number) => n.toLocaleString('pt-BR');
/** preço com a moeda: gold dourado com "gp", coins em azul com "NC" (Noxara Coins) */
const pc = (n: number, c?: MarketCurrency) => c === 'coins'
  ? <span class="mkt-nc" title="Noxara Coins">{n0(n)} <small>NC</small></span>
  : <span class="mkt-gp">{n0(n)} <small>gp</small></span>;
const RCLS = ['', 'mkt-r1', 'mkt-r2', 'mkt-r3'];
const DAY = 86400_000;
const dt = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const ends = (at: number) => dt(new Date(at + MARKET_OFFER_DAYS * DAY));
const when = (at: number) => dt(new Date(at));
/** tempo que falta para a oferta vencer: texto curto e fração (0..1) do prazo de 7 dias */
const remain = (at: number) => {
  const ms = at + MARKET_OFFER_DAYS * DAY - Date.now();
  if (ms <= 0) return { txt: 'vencendo', pct: 0 };
  const d = Math.floor(ms / DAY), h = Math.floor((ms % DAY) / 3600_000), m = Math.floor((ms % 3600_000) / 60_000);
  return { txt: d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${Math.max(1, m)}m`, pct: Math.min(1, ms / (MARKET_OFFER_DAYS * DAY)) };
};
const STATE: Record<MarketHistoryRow['state'], string> = { completed: 'Concluída', cancelled: 'Cancelada', expired: 'Vencida' };

function nameOf(typeId: number) { return typeId === MARKET_COIN_ITEM ? 'Noxara Coins' : catalogNow()?.get(typeId)?.name ?? `item ${typeId}`; }
function offerName(o: { typeId: number; rarity: number; upgrade?: number }) {
  return `${nameOf(o.typeId)}${o.upgrade ? ' +' + o.upgrade : ''}${o.rarity ? ` (${RARITY_NAMES[o.rarity]})` : ''}`;
}
/** item que pode ir para o mercado (carregado ou no depot, não preso, recipiente vazio, não equipado) */
function canTrade(items: ItemInstance[], i: ItemInstance) {
  if (i.bound || i.place === 'equip' || i.place === 'pouch' || items.some((c) => c.parentId === i.id)) return false;
  const t = catalogNow()?.get(i.typeId);
  return !!t && marketCategory(t) !== undefined && !/ coins?$/i.test(t.name);
}

export function Market(props: {
  offers: MarketOffer[]; me: number; items: ItemInstance[]; bank: number; coins: number; level: number; vocation: string;
  history: MarketHistoryRow[]; stats: MarketStats | null; send: Send;
}) {
  const cat = catalogNow();
  const [view, setView] = useState<'market' | 'all' | 'mine'>('market');
  const [allQ, setAllQ] = useState('');
  const [allSort, setAllSort] = useState<'new' | 'name' | 'cheap' | 'dear'>('new');
  const [category, setCategory] = useState(1);
  const [q, setQ] = useState('');
  const [fVoc, setFVoc] = useState(false);
  const [fLvl, setFLvl] = useState(false);
  const [fOwn, setFOwn] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const [tab, setTab] = useState<'offers' | 'details' | 'stats'>('offers');
  const [pick, setPick] = useState<{ side: 'sell' | 'buy'; id: number } | null>(null);
  const [acceptN, setAcceptN] = useState(1);
  const [mode, setMode] = useState<'sell' | 'buy'>('sell');
  const [price, setPrice] = useState(0);
  const [amount, setAmount] = useState(1);
  const [anon, setAnon] = useState(false);
  const [curr, setCurr] = useState<MarketCurrency>('gold');
  const [stackId, setStackId] = useState<number | null>(null);
  const [mineTab, setMineTab] = useState<'current' | 'history'>('current');
  const [help, setHelp] = useState(false);

  // quanto de cada item o jogador tem para vender (mochila + depot)
  const owned = useMemo(() => {
    const m = new Map<number, number>();
    for (const i of props.items) if (canTrade(props.items, i)) m.set(i.typeId, (m.get(i.typeId) ?? 0) + i.count);
    m.set(MARKET_COIN_ITEM, props.coins);
    return m;
  }, [props.items, props.coins]);

  // todos os itens negociáveis do jogo por categoria (como a lista do Market do cliente)
  const all = useMemo(() => {
    const list: ItemType[] = [];
    if (!cat) return list;
    for (const t of cat.values()) if (marketCategory(t) !== undefined && (t.id === MARKET_COIN_ITEM || !/ coins?$/i.test(t.name))) list.push(t);
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [cat]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return all.filter((t) => (s ? t.name.toLowerCase().includes(s) : marketCategory(t) === category)
      && (!fVoc || !t.vocations?.length || t.vocations.includes(props.vocation))
      && (!fLvl || !t.level || t.level <= props.level)
      && (!fOwn || (owned.get(t.id) ?? 0) > 0));
  }, [all, q, category, fVoc, fLvl, fOwn, owned, props.vocation, props.level]);

  const item = sel !== null ? cat?.get(sel) : undefined;
  const isCoin = sel === MARKET_COIN_ITEM;
  const byCur = (a: MarketOffer, b: MarketOffer) => (a.currency === b.currency ? 0 : a.currency === 'coins' ? 1 : -1);
  const sells = props.offers.filter((o) => o.side === 'sell' && o.typeId === sel).sort((a, b) => byCur(a, b) || a.price - b.price);
  const buys = props.offers.filter((o) => o.side === 'buy' && o.typeId === sel).sort((a, b) => byCur(a, b) || b.price - a.price);
  const mineSell = props.offers.filter((o) => o.sellerId === props.me && o.side === 'sell');
  const mineBuy = props.offers.filter((o) => o.sellerId === props.me && o.side === 'buy');
  const stacks = sel !== null && !isCoin ? props.items.filter((i) => i.typeId === sel && canTrade(props.items, i)).sort((a, b) => a.rarity - b.rarity || b.count - a.count) : [];
  const stack = stacks.find((i) => i.id === stackId) ?? stacks[0];
  const maxAmount = mode === 'sell' ? (isCoin ? props.coins : stack?.count ?? 0) : (item?.stackable || isCoin ? 64000 : 100);
  const cc: MarketCurrency = isCoin ? 'gold' : curr;   // as próprias Noxara Coins só por gold
  const wallet = cc === 'coins' ? props.coins : props.bank;
  const total = price * amount, fee = price > 0 && amount > 0 ? marketFee(total, cc) : 0;
  const picked = pick ? props.offers.find((o) => o.id === pick.id) : undefined;
  const allSells = props.offers.filter((o) => o.side === 'sell');

  useEffect(() => { if (sel !== null) props.send({ op: 'stats', typeId: sel }); setPick(null); setAmount(1); setStackId(null); }, [sel]);
  useEffect(() => { if (view === 'mine') props.send({ op: 'history' }); }, [view]);

  const choose = (id: number) => { setSel(id); setTab('offers'); setMode((owned.get(id) ?? 0) > 0 ? 'sell' : 'buy'); };
  const create = () => {
    if (!sel || price < 1 || amount < 1) return;
    if (mode === 'buy') props.send({ op: 'createBuy', typeId: sel, count: amount, price, anonymous: anon, currency: cc });
    else if (isCoin) props.send({ op: 'sellCoins', count: amount, price, anonymous: anon });
    else if (stack) props.send({ op: 'sell', itemId: stack.id, count: Math.min(amount, stack.count), price, anonymous: anon, currency: cc });
  };
  const accept = () => {
    if (!picked) return;
    const n = Math.max(1, Math.min(picked.count, acceptN));
    props.send({ op: picked.side === 'sell' ? 'buy' : 'sellTo', offerId: picked.id, count: n });
    setPick(null);
  };

  /** ícone do item num slot com a borda da raridade */
  const slot = (typeId: number, rarity = 0, count = 1, cls = '') => <span class={`mkt-slot mkt-rb${rarity} ${cls}`}><ItemImg typeId={typeId} count={count} /></span>;
  /** prazo restante: texto + barrinha (a data exata fica no título) */
  const timer = (at: number) => {
    const r = remain(at);
    return <span class={'mkt-time' + (r.pct < 0.15 ? ' low' : '')} title={`Termina em ${ends(at)}`}>{r.txt}<i><b style={{ width: r.pct * 100 + '%' }} /></i></span>;
  };
  /** uma oferta como linha/card: ícone, nome/vendedor, quantidade, preço (un. em destaque, total abaixo) e prazo */
  const offerRow = (o: MarketOffer, opts: { mine?: boolean; showItem?: boolean; onClick: () => void }) => (
    <div key={o.id} class={'mkt-row' + (pick?.id === o.id ? ' on' : '') + (o.sellerId === props.me && !opts.mine ? ' own' : '')} onClick={opts.onClick}>
      {slot(o.typeId, o.rarity)}
      <span class="mkt-who">
        {opts.mine || opts.showItem
          ? <><b class={RCLS[o.rarity]}>{offerName(o)}</b><small>{opts.mine ? (o.side === 'sell' ? 'Venda' : 'Compra') : o.seller}{o.sellerId === props.me && !opts.mine ? <em class="mkt-you">você</em> : null}</small></>
          : <><b class={RCLS[o.rarity]}>{o.seller}{o.sellerId === props.me ? <em class="mkt-you">você</em> : null}</b><small>{o.rarity ? RARITY_NAMES[o.rarity] : 'Comum'}{o.upgrade ? ` · +${o.upgrade}` : ''}</small></>}
      </span>
      <span class="mkt-qty" title="Quantidade">×{n0(o.count)}</span>
      <span class="mkt-price"><b>{pc(o.price, o.currency)}</b><small>total {pc(o.price * o.count, o.currency)}</small></span>
      {timer(o.at)}
    </div>
  );
  const offerList = (title: string, icon: string, list: MarketOffer[], side: 'sell' | 'buy', mine?: boolean, empty = 'Nenhuma oferta.') => (
    <div class={'mkt-box ' + (side === 'sell' ? 'sell' : 'buy')}>
      <div class="mkt-cap"><i>{icon}</i>{title}<span>{list.length}</span></div>
      <div class="mkt-list">
        {list.map((o) => offerRow(o, { mine, onClick: () => { setPick({ side, id: o.id }); setAcceptN(1); } }))}
        {!list.length && <div class="mkt-empty">{empty}</div>}
      </div>
    </div>
  );
  /** caixa de estatística: faixa mín–máx com o marcador da média */
  const statBox = (key: string, title: string, st: MarketStatSide, coin: boolean) => {
    const u = coin ? 'NC' : 'gp', hi = Math.max(1, st.highest);
    const lo = (st.lowest / hi) * 100, avg = (st.average / hi) * 100;
    return (
      <div key={key} class={'mkt-stat' + (coin ? ' coin' : '')}>
        <div class="mkt-cap">{title}<span>30 dias</span></div>
        <div class="mkt-stat-avg"><small>Preço médio</small><b>{n0(st.average)} <small>{u}</small></b></div>
        <div class="mkt-range" title={`De ${n0(st.lowest)} a ${n0(st.highest)} ${u}; média ${n0(st.average)} ${u}`}>
          <i style={{ left: lo + '%', width: Math.max(1, 100 - lo) + '%' }} />
          <em style={{ left: `calc(${avg}% - 1px)` }} />
        </div>
        <div class="mkt-range-lbl"><span>mín. <b>{n0(st.lowest)}</b></span><span>máx. <b>{n0(st.highest)}</b></span></div>
        <div class="mkt-stat-kv"><span>Negócios <b>{n0(st.transactions)}</b></span><span>Volume <b>{n0(st.total)} {u}</b></span></div>
      </div>
    );
  };

  return (
    <div class="mkt">
      {/* cabeçalho: balcão de madeira com a balança, saldos em destaque */}
      <div class="mkt-head">
        <span class="mkt-medal" aria-hidden="true">⚖️</span>
        <div class="mkt-head-txt">
          <div class="mkt-title">Mercado de Noxara <button class="mkt-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <small>Compre e venda com outros jogadores, por gold ou Noxara Coins.</small>
        </div>
        <div class="mkt-wallet">
          <span class="mkt-purse gold" title="Saldo do banco"><i class="mkt-coin" /><span><small>Banco</small><b>{n0(props.bank)}</b></span></span>
          <span class="mkt-purse nc" title="Noxara Coins"><ItemImg typeId={MARKET_COIN_ITEM} count={1} /><span><small>Noxara Coins</small><b>{n0(props.coins)}</b></span></span>
        </div>
      </div>
      {help && (
        <div class="mkt-steps">
          <div><i>🔎</i><b>1. Escolha o item</b><small>Pela categoria, pela busca ou em "Todas à venda". Itens com borda dourada têm ofertas abertas.</small></div>
          <div><i>🤝</i><b>2. Aceite uma oferta</b><small>Clique numa venda para comprar ou numa compra para vender. O que você compra vai para o depot.</small></div>
          <div><i>📜</i><b>3. Ou anuncie</b><small>Defina preço e quantidade. A taxa é de 5% e não volta. O anúncio vale {MARKET_OFFER_DAYS} dias.</small></div>
          <div><i>⏳</i><b>4. Acompanhe</b><small>Em "Minhas ofertas" você cancela quando quiser e vê o histórico. Ofertas vencidas voltam para você.</small></div>
        </div>
      )}

      <div class="mkt-nav">
        <button class={'tb-button' + (view === 'market' ? ' green' : '')} onClick={() => setView('market')}>Mercado</button>
        <button class={'tb-button' + (view === 'all' ? ' green' : '')} onClick={() => setView('all')} title="Todos os itens anunciados para venda agora">Todas à venda ({allSells.length})</button>
        <button class={'tb-button' + (view === 'mine' ? ' green' : '')} onClick={() => setView('mine')}>Minhas ofertas ({mineSell.length + mineBuy.length})</button>
      </div>

      {view === 'all' ? (
        <div class="mkt-panel">
          <div class="mkt-toolbar">
            <input class="mkt-input grow" placeholder="Filtrar por item ou vendedor" value={allQ} onInput={(e) => setAllQ((e.target as HTMLInputElement).value)} />
            <select class="mkt-input" value={allSort} onChange={(e) => setAllSort((e.target as HTMLSelectElement).value as typeof allSort)}>
              <option value="new">Mais recentes</option><option value="name">Nome do item</option><option value="cheap">Menor preço</option><option value="dear">Maior preço</option>
            </select>
          </div>
          <div class="mkt-box sell">
            <div class="mkt-cap"><i>🏷️</i>Todas as ofertas de venda<span>clique para abrir o item e comprar</span></div>
            <div class="mkt-list tall">
              {allSells.filter((o) => !allQ.trim() || offerName(o).toLowerCase().includes(allQ.trim().toLowerCase()) || o.seller.toLowerCase().includes(allQ.trim().toLowerCase()))
                .sort((a, b) => allSort === 'name' ? offerName(a).localeCompare(offerName(b)) || a.price - b.price : allSort === 'cheap' ? a.price - b.price : allSort === 'dear' ? b.price - a.price : b.at - a.at)
                .slice(0, 500).map((o) => offerRow(o, {
                  showItem: true,
                  onClick: () => { setView('market'); setQ(''); const t = cat?.get(o.typeId); if (t) { const mc = marketCategory(t); if (mc) setCategory(mc); } choose(o.typeId); setTimeout(() => { setPick({ side: 'sell', id: o.id }); setAcceptN(1); }, 0); },
                }))}
              {!allSells.length && <div class="mkt-empty">Nenhum item à venda no momento.</div>}
            </div>
          </div>
        </div>
      ) : view === 'market' ? (
        <div class="mkt-body">
          <div class="mkt-panel mkt-browse">
            <select class="mkt-input" value={q ? '' : category} onChange={(e) => { setCategory(Number((e.target as HTMLSelectElement).value)); setQ(''); }}>
              {q && <option value="">Resultado da busca</option>}
              {MARKET_CATEGORIES.filter((c) => !c.group).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              <optgroup label="Armas">{MARKET_CATEGORIES.filter((c) => c.group).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</optgroup>
            </select>
            <input class="mkt-input" placeholder="🔎 Procurar item" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
            <div class="mkt-chips">
              <label class={fVoc ? 'on' : ''} title="Só itens que a sua vocation usa"><input type="checkbox" checked={fVoc} onChange={() => setFVoc(!fVoc)} /> Vocação</label>
              <label class={fLvl ? 'on' : ''} title="Só itens do seu level ou menos"><input type="checkbox" checked={fLvl} onChange={() => setFLvl(!fLvl)} /> Level</label>
              <label class={fOwn ? 'on' : ''} title="Só itens que você tem na mochila ou no depot"><input type="checkbox" checked={fOwn} onChange={() => setFOwn(!fOwn)} /> Meus itens</label>
            </div>
            <div class="mkt-grid">
              {shown.slice(0, 600).map((t) => (
                <span key={t.id} class={'mkt-cell' + (sel === t.id ? ' on' : '') + (props.offers.some((o) => o.typeId === t.id) ? ' has' : '')} title={t.name} onClick={() => choose(t.id)}>
                  <ItemImg typeId={t.id} count={1} />
                  {(owned.get(t.id) ?? 0) > 0 && <i>{owned.get(t.id)! > 999 ? '1k+' : owned.get(t.id)}</i>}
                </span>
              ))}
              {!shown.length && <div class="mkt-empty">Nenhum item.</div>}
            </div>
            <div class="mkt-hint">{shown.length > 600 ? `Mostrando 600 de ${n0(shown.length)}: use a busca.` : `${n0(shown.length)} itens`} · <span class="mkt-has-dot" /> com ofertas</div>
          </div>

          <div class="mkt-main">
            {!item ? (
              <div class="mkt-none"><span>⚖️</span><b>Escolha um item</b><small>Clique num item da lista para ver quem vende, quem compra e quanto ele costuma valer.</small></div>
            ) : (
              <>
                <div class="mkt-item">
                  {slot(item.id, 0, 1, 'big')}
                  <div class="mkt-item-txt">
                    <b>{nameOf(item.id)}</b>
                    <small>
                      Você tem <b>{n0(owned.get(item.id) ?? 0)}</b>
                      {' · '}{sells.length} à venda · {buys.length} procurando
                      {sells[0] ? <> · a partir de {pc(sells[0].price, sells[0].currency)}</> : null}
                    </small>
                  </div>
                  <div class="mkt-tabs">
                    {([['offers', 'Ofertas'], ['details', 'Detalhes'], ['stats', 'Estatísticas']] as const).map(([id, l]) =>
                      <button key={id} class={'tb-button' + (tab === id ? ' green' : '')} onClick={() => setTab(id)}>{l}</button>)}
                  </div>
                </div>

                {tab === 'offers' && (
                  <>
                    {offerList('À venda', '🏷️', sells, 'sell')}
                    {offerList('Procura-se (ofertas de compra)', '📣', buys, 'buy')}

                    <div class={'mkt-deal' + (picked && picked.sellerId !== props.me ? ' live' : '')}>
                      {picked && picked.sellerId !== props.me ? (
                        <>
                          {slot(picked.typeId, picked.rarity)}
                          <div class="mkt-deal-txt">
                            <b>{picked.side === 'sell' ? `Comprar de ${picked.seller}` : `Vender para ${picked.seller}`}</b>
                            <small>{pc(picked.price, picked.currency)} cada · disponível {n0(picked.count)}</small>
                          </div>
                          <div class="mkt-deal-qty">
                            <input class="mkt-input num" type="number" min={1} max={picked.count} value={acceptN} onInput={(e) => setAcceptN(Math.max(1, Math.min(picked.count, Number((e.target as HTMLInputElement).value) || 1)))} />
                            <input type="range" min={1} max={picked.count} value={acceptN} onInput={(e) => setAcceptN(Number((e.target as HTMLInputElement).value))} />
                          </div>
                          <div class="mkt-deal-total"><small>Total</small><b>{pc(picked.price * acceptN, picked.currency)}</b></div>
                          <button class="tb-button green" disabled={picked.side === 'sell' ? (picked.currency === 'coins' ? props.coins : props.bank) < picked.price * acceptN : (owned.get(picked.typeId) ?? 0) < acceptN} onClick={accept}>Aceitar</button>
                        </>
                      ) : <span class="mkt-muted">{picked ? 'Essa oferta é sua (cancele em Minhas ofertas).' : '👆 Clique numa oferta acima para comprar ou vender.'}</span>}
                    </div>

                    <div class="mkt-form">
                      <div class="mkt-cap"><i>📜</i>Anunciar<span>vale {MARKET_OFFER_DAYS} dias</span></div>
                      <div class="mkt-form-row">
                        <div class="mkt-seg">
                          <button class={mode === 'sell' ? 'on' : ''} onClick={() => setMode('sell')}>Vender</button>
                          <button class={mode === 'buy' ? 'on' : ''} onClick={() => setMode('buy')}>Comprar</button>
                        </div>
                        {!isCoin && (
                          <div class="mkt-seg cur">
                            <button class={curr === 'gold' ? 'on gold' : 'gold'} onClick={() => setCurr('gold')}>Gold</button>
                            <button class={curr === 'coins' ? 'on nc' : 'nc'} onClick={() => setCurr('coins')}>Noxara Coins</button>
                          </div>
                        )}
                        <label class="mkt-anon" title="O seu nome não aparece na oferta"><input type="checkbox" checked={anon} onChange={() => setAnon(!anon)} /> Anônimo</label>
                      </div>
                      {mode === 'sell' && !isCoin && stacks.length > 1 && (
                        <div class="mkt-stacks"><small>Qual pilha:</small>
                          {stacks.map((i) => <span key={i.id} class={`mkt-slot mkt-rb${i.rarity}` + (stack?.id === i.id ? ' on' : '')} title={offerName(i)} onClick={() => setStackId(i.id)}><ItemImg typeId={i.typeId} count={i.count} /></span>)}
                        </div>
                      )}
                      <div class="mkt-form-grid">
                        <label class="mkt-field"><small>Preço por unidade ({cc === 'coins' ? 'NC' : 'gp'})</small>
                          <input class={'mkt-input num ' + (cc === 'coins' ? 'nc' : 'gold')} type="number" min={1} max={MARKET_MAX_PRICE} value={price || ''} onInput={(e) => setPrice(Math.max(0, Math.min(MARKET_MAX_PRICE, Math.floor(Number((e.target as HTMLInputElement).value) || 0))))} />
                        </label>
                        <label class="mkt-field"><small>Quantidade {mode === 'sell' ? `(máx. ${n0(maxAmount)})` : ''}</small>
                          <input class="mkt-input num" type="number" min={1} max={maxAmount} value={amount} onInput={(e) => setAmount(Math.max(1, Math.min(Math.max(1, maxAmount), Math.floor(Number((e.target as HTMLInputElement).value) || 1))))} />
                        </label>
                      </div>
                      {maxAmount > 1 && <input class="mkt-range-in" type="range" min={1} max={maxAmount} value={Math.min(amount, maxAmount)} onInput={(e) => setAmount(Number((e.target as HTMLInputElement).value))} />}
                      <div class="mkt-receipt">
                        <div><span>Valor total</span><b>{pc(total, cc)}</b></div>
                        <div><span>Taxa do anúncio (5%, não volta)</span><b class="fee">− {pc(fee, cc)}</b></div>
                        <div class="sum"><span>{mode === 'sell' ? 'Paga agora (só a taxa)' : 'Reservado agora (total + taxa)'}</span><b>{pc(mode === 'sell' ? fee : total + fee, cc)}</b></div>
                        <div class="dim"><span>Seu saldo</span><b>{pc(wallet, cc)}</b></div>
                        <div class="dim"><span>Duração</span><b>{MARKET_OFFER_DAYS} dias · vence em {ends(Date.now())}</b></div>
                      </div>
                      <div class="mkt-form-foot">
                        <span class="mkt-hint">{mode === 'sell'
                          ? (maxAmount < 1 ? (isCoin ? 'Você não tem Noxara Coins.' : 'Você não tem esse item na mochila ou no depot.') : `Você tem ${n0(owned.get(item.id) ?? 0)}. O item sai na hora e volta se você cancelar ou se a oferta vencer (${MARKET_OFFER_DAYS} dias).`)
                          : cc === 'coins' ? 'As Noxara Coins (total + taxa) saem da sua conta na hora e ficam reservadas. O que você comprar vai para o seu depot.' : 'O gold (total + taxa) sai do banco na hora e fica reservado. O que você comprar vai para o seu depot.'} {cc === 'coins' ? 'A taxa é de 5% em Noxara Coins (mín. 1) e não volta.' : 'A taxa é de 5% (mín. 20, máx. 1kk) e não volta.'}</span>
                        <button class="tb-button green mkt-create" disabled={price < 1 || amount < 1 || (mode === 'sell' ? maxAmount < 1 || wallet < fee : wallet < total + fee)} onClick={create}>{mode === 'sell' ? 'Anunciar venda' : 'Anunciar compra'}</button>
                      </div>
                    </div>
                  </>
                )}

                {tab === 'details' && (
                  <pre class="mkt-details">{isCoin ? 'Noxara Coins: a moeda da loja do jogo. Pode ser negociada aqui no mercado, por gold.' : describeItem(item, { count: 1, rarity: 0 })}</pre>
                )}

                {tab === 'stats' && (
                  <div class="mkt-stats">
                    {props.stats?.typeId === item.id ? (['gold', 'coins'] as const).flatMap((c) => (['sell', 'buy'] as const).map((s) => {
                      const st = c === 'gold' ? props.stats![s] : props.stats!.coins?.[s];
                      if (!st || (c === 'coins' && !st.transactions)) return null;
                      return statBox(c + s, `${s === 'sell' ? 'Ofertas de venda aceitas' : 'Ofertas de compra aceitas'}${c === 'coins' ? ' em Noxara Coins' : ''}`, st, c === 'coins');
                    })) : <div class="mkt-empty">Carregando...</div>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div class="mkt-panel">
          <div class="mkt-tabs">
            <button class={'tb-button' + (mineTab === 'current' ? ' green' : '')} onClick={() => setMineTab('current')}>Ofertas atuais</button>
            <button class={'tb-button' + (mineTab === 'history' ? ' green' : '')} onClick={() => setMineTab('history')}>Histórico</button>
          </div>
          {mineTab === 'current' ? (
            <>
              {offerList('Minhas ofertas de venda', '🏷️', mineSell, 'sell', true)}
              {offerList('Minhas ofertas de compra', '📣', mineBuy, 'buy', true)}
              <div class={'mkt-deal' + (picked && picked.sellerId === props.me ? ' live' : '')}>
                {picked && picked.sellerId === props.me
                  ? <>{slot(picked.typeId, picked.rarity)}<div class="mkt-deal-txt"><b class={RCLS[picked.rarity]}>{offerName(picked)}</b><small>{n0(picked.count)} × {pc(picked.price, picked.currency)} · termina em {ends(picked.at)}</small></div><button class="tb-button" onClick={() => { props.send({ op: 'cancel', offerId: picked.id }); setPick(null); }}>Cancelar oferta</button></>
                  : <span class="mkt-muted">Clique numa oferta para cancelar quando quiser. A taxa do anúncio não é devolvida. Ofertas vencem em {MARKET_OFFER_DAYS} dias.</span>}
              </div>
            </>
          ) : (
            <div class="mkt-box">
              <div class="mkt-cap"><i>📒</i>Histórico de negócios<span>{props.history.length}</span></div>
              <div class="mkt-list tall">
                {props.history.map((h, i) => (
                  <div key={i} class="mkt-row hist">
                    {slot(h.typeId, h.rarity)}
                    <span class="mkt-who"><b class={RCLS[h.rarity]}>{offerName(h)}</b><small>{h.side === 'sell' ? 'Venda' : 'Compra'} · {when(h.at)}</small></span>
                    <span class="mkt-qty">×{n0(h.count)}</span>
                    <span class="mkt-price"><b>{pc(h.price, h.currency)}</b><small>por unidade</small></span>
                    <span class={'mkt-state ' + h.state}>{STATE[h.state]}</span>
                  </div>
                ))}
                {!props.history.length && <div class="mkt-empty">Nenhum negócio ainda.</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
